import {
  DeployChannelStats,
  DeployContentKind,
  DeployCountKind,
  DeployPlanMessagesResponse
} from '@core/models/deploy-plan-config.model';

import { failedWithoutSending, hasSuccessfulSend } from './deploy-selection.utils';

/**
 * The counts a loaded `/messages` response already implies.
 *
 * GBE persists these per deploy, but a status row written before it started doing so carries
 * none — and there is no way to backfill the denominator, since it is only known at send time.
 * This reads the same send history the response embeds, so a channel the operator has opened
 * reports honestly without waiting for another deploy.
 *
 * `null` where there is no ratio to show: a `view`-kind channel sends one payload per
 * configuration, and a channel with no messages would report "0 of 0 sent".
 */
export function deriveChannelStats(
  response: DeployPlanMessagesResponse | null
): DeployChannelStats | null {
  if (response === null || response.contentKind !== DeployContentKind.MESSAGES) return null;

  const messages = response.content.messages;
  if (messages.length === 0) return null;

  let sentCount = 0;
  let failedCount = 0;
  for (const message of messages) {
    // Sent and failed never overlap, and a message that failed and was then re-sent is one
    // sent message — matching how GBE counts, so the two sources cannot disagree. The same two
    // helpers answer for the channel's failing-message list, which is what keeps its count and
    // this badge from drifting apart.
    if (hasSuccessfulSend(message)) sentCount += 1;
    else if (failedWithoutSending(message)) failedCount += 1;
  }

  // Counting the messages one by one is what `PER_MESSAGE` means, so this branch cannot
  // produce anything else.
  return {
    countKind: DeployCountKind.PER_MESSAGE,
    totalMessages: messages.length,
    sentCount,
    failedCount
  };
}
